import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Brush,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  angleDomain,
  buildPoseAngleChartData,
  numericDomain,
} from "../../utils/poseAngleSeries";
import {
  buildStrokeClusters,
  MIN_RALLY_STROKES,
  rallyWindowMs,
  phasesOfStroke,
  resolveFhTopspinPhaseAtMs,
  resolveFhPhaseAtMs,
} from "../../utils/poseHeuristics";
import { servePlayerOf } from "../../utils/serveHeuristics";
import { PoseDetectionMetrics } from "./PoseDetectionMetrics";
import { ServeHeatMap } from "./ServeHeatMap";
import { ServeBounceHeatMap } from "./ServeBounceHeatMap";
import "./poseOverlays.css";

const COLOR_A = "#00bcd4";
const COLOR_B = "#ff4081";
const COLOR_A_SHOULDER = "#fbbf24";
const COLOR_A_SPAN = "#67e8f9";
const COLOR_B_SPAN = "#f9a8d4";
const COLOR_A_HE_DX = "#c4b5fd";
const COLOR_A_SW = "#f0abfc";
const COLOR_STROKE = "#fb923c";
const COLOR_STROKE_FH = "#38bdf8";
const COLOR_STROKE_BH = "#a78bfa";
const COLOR_STROKE_BHD = "#34d399";
/** Serve toss markers — rose, distinct from orange FH topspin peaks. */
const COLOR_SERVE = "#fb7185";
// Stroke phase bands (subtle timing envelopes)
const COLOR_PHASE_PREP_FILL = "rgba(56, 189, 248, 0.18)";
const COLOR_PHASE_PREP_STROKE = "rgba(56, 189, 248, 0.72)";
const COLOR_PHASE_FORWARD_FILL = "rgba(251, 191, 36, 0.16)";
const COLOR_PHASE_FORWARD_STROKE = "rgba(251, 191, 36, 0.72)";
const COLOR_PHASE_CONTACT_FILL = "rgba(251, 146, 60, 0.20)";
const COLOR_PHASE_CONTACT_STROKE = "rgba(251, 146, 60, 0.92)";
const COLOR_PHASE_FOLLOW_FILL = "rgba(255, 64, 129, 0.14)";
const COLOR_PHASE_FOLLOW_STROKE = "rgba(255, 64, 129, 0.70)";
const COLOR_PHASE_RECOVERY_FILL = "rgba(34, 197, 94, 0.12)";
const COLOR_PHASE_RECOVERY_STROKE = "rgba(34, 197, 94, 0.68)";
/** Trial / essai bands — strong violet, distinct from cyan Δx + orange strokes. */
const COLOR_TRIAL_FILL = "rgba(168, 85, 247, 0.28)";
const COLOR_TRIAL_STROKE = "rgba(216, 180, 254, 0.95)";
const COLOR_TRIAL_LABEL = "#faf5ff";
const COLOR_RALLY_FILL = "rgba(147, 51, 234, 0.42)";
const COLOR_RALLY_STROKE = "#e9d5ff";
const COLOR_RALLY_LABEL = "#ffffff";
const RALLY_PAD_SEC = 0.12;

const FSM_CURVE_KEYS = {
  playerARacketShoulder: true,
  playerARacketElbowInterior: true,
  playerAShoulderSpan: true,
  playerAHandElbowDx: true,
  playerAHandElbowDy: true,
  playerAShoulderWrist: true,
  playerATossWristX: true,
  playerATossWristY: true,
  playerBTossWristX: true,
  playerBTossWristY: true,
};

/** Series each detector actually reads for prep / peak / contact / recovery. */
const STROKE_KIND_CURVE_KEYS = {
  all: [
    "playerARacketShoulder",
    "playerAShoulderSpan",
    "playerAHandElbowDx",
    "playerAHandElbowDy",
    "playerARacketElbowInterior",
    "playerAShoulderWrist",
    "playerATossWristY",
    "playerBTossWristY",
  ],
  ts: ["playerARacketShoulder", "playerAShoulderSpan"],
  fh: ["playerARacketShoulder", "playerAShoulderSpan"],
  bh: ["playerARacketShoulder", "playerAHandElbowDx", "playerAHandElbowDy"],
  bhd: [
    "playerARacketShoulder",
    "playerARacketElbowInterior",
    "playerAShoulderWrist",
    "playerAHandElbowDy",
  ],
  srv: [
    "playerATossWristY",
    "playerBTossWristY",
    "playerATossWristX",
    "playerBTossWristX",
  ],
};

function curvesForStrokeKind(kind) {
  const keys = STROKE_KIND_CURVE_KEYS[kind] || STROKE_KIND_CURVE_KEYS.all;
  const next = { ...FSM_CURVE_KEYS };
  Object.keys(next).forEach((key) => {
    next[key] = false;
  });
  keys.forEach((key) => {
    next[key] = true;
  });
  return next;
}

const DEFAULT_CURVES = curvesForStrokeKind("all");

function formatSec(v) {
  if (!Number.isFinite(v)) return "";
  if (v < 60) return `${v.toFixed(1)}s`;
  const m = Math.floor(v / 60);
  const s = Math.round(v % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** Build chart markers from FH topspin stroke list. */
function strokePeakMs(stroke) {
  const tPeak = Number(stroke?.t_peak_ms);
  if (Number.isFinite(tPeak)) return tPeak;
  const tShoulder = Number(stroke?.t_shoulder_ms);
  const tAngle = Number(stroke?.t_angle_ms);
  if (Number.isFinite(tShoulder) && Number.isFinite(tAngle)) {
    return Math.round((tShoulder + tAngle) / 2);
  }
  return Number.isFinite(tShoulder) ? tShoulder : null;
}

function strokeMarkersFromDetection(detection, { color = COLOR_STROKE, keyPrefix = "ts" } = {}) {
  const strokes = Array.isArray(detection?.strokes) ? detection.strokes : [];
  return strokes
    .map((s, i) => {
      const tPeakMs = strokePeakMs(s);
      if (!Number.isFinite(tPeakMs)) return null;
      return {
        key: `${keyPrefix}-stroke-${i + 1}`,
        kind: keyPrefix,
        strokeIndex: i + 1,
        index: i + 1,
        tPeakSec: tPeakMs / 1000,
        amplitudeMs: Number(s?.amplitude_ms) || null,
        color: Array.isArray(s?.tags) && s.tags.includes("topspin") ? COLOR_STROKE : color,
        tags: Array.isArray(s?.tags) ? s.tags : [],
        labelPrefix:
          keyPrefix === "fh" ? "F" : keyPrefix === "bh" ? "B" : keyPrefix === "bhd" ? "D" : "",
      };
    })
    .filter(Boolean);
}

function phaseBandsFromDetection(detection, { keyPrefix = "ts" } = {}) {
  const strokes = Array.isArray(detection?.strokes) ? detection.strokes : [];
  if (!strokes.length) return [];

  const out = [];
  strokes.forEach((s, i) => {
    const phases = phasesOfStroke(s);
    if (!phases || typeof phases !== "object") return;
    const add = (phaseKey, fill, stroke) => {
      const r = phases?.[phaseKey];
      if (!r) return;
      const t0 = Number(r?.t_start_ms);
      const t1 = Number(r?.t_end_ms);
      if (!Number.isFinite(t0) || !Number.isFinite(t1)) return;
      if (t1 < t0) return;
      out.push({
        key: `${keyPrefix}-phase-${i + 1}-${phaseKey}`,
        kind: keyPrefix,
        strokeIndex: i + 1,
        phaseKey,
        tStartSec: t0 / 1000,
        tEndSec: t1 / 1000,
        fill,
        stroke,
      });
    };

    add("preparation", COLOR_PHASE_PREP_FILL, COLOR_PHASE_PREP_STROKE);
    add("forward_swing", COLOR_PHASE_FORWARD_FILL, COLOR_PHASE_FORWARD_STROKE);
    add("contact", COLOR_PHASE_CONTACT_FILL, COLOR_PHASE_CONTACT_STROKE);
    add("follow_through", COLOR_PHASE_FOLLOW_FILL, COLOR_PHASE_FOLLOW_STROKE);
    add("recovery", COLOR_PHASE_RECOVERY_FILL, COLOR_PHASE_RECOVERY_STROKE);
  });
  return out;
}

function contactMarkersFromDetection(detection, { keyPrefix = "ts" } = {}) {
  const strokes = Array.isArray(detection?.strokes) ? detection.strokes : [];
  if (!strokes.length) return [];
  const out = [];
  strokes.forEach((s, i) => {
    const tContactMs = Number(s?.t_contact_ms);
    if (!Number.isFinite(tContactMs)) return;
    out.push({
      key: `${keyPrefix}-contact-${i + 1}`,
      kind: keyPrefix,
      strokeIndex: i + 1,
      index: i + 1,
      tContactSec: tContactMs / 1000,
      stroke: COLOR_PHASE_CONTACT_STROKE,
    });
  });
  return out;
}

function serveLabel(event, fallbackIndex = 1) {
  const player = servePlayerOf(event);
  const n = Number(event?.player_index) || fallbackIndex;
  return `S${player}${n}`;
}

function serveMarkersFromDetection(detection) {
  const events = Array.isArray(detection?.events) ? detection.events : [];
  return events
    .map((event, i) => {
      const tMs = Number(event?.t_peak_ms ?? event?.t_ms);
      if (!Number.isFinite(tMs)) return null;
      const index = Number(event?.index) || i + 1;
      const player = servePlayerOf(event);
      const playerIndex = Number(event?.player_index) || index;
      return {
        key: `srv-serve-${index}`,
        kind: "srv",
        index,
        player,
        tPeakSec: tMs / 1000,
        confidence: Number(event?.confidence) || null,
        serverSide: player === "B" ? "right" : "left",
        color: player === "B" ? COLOR_B : COLOR_A,
        readyMs: serveReadyMetric(event),
        label: serveLabel(event, playerIndex),
        event,
      };
    })
    .filter(Boolean);
}

/** All attempt clusters as bands: T#-n (essai) or T#-R#-n (rally ≥3). */
function clusterBandsFromDetection(detection, { keyPrefix = "ts" } = {}) {
  const strokes = Array.isArray(detection?.strokes) ? detection.strokes : [];
  if (!strokes.length) return [];
  const gapMs =
    Number(detection?.rally_window_ms) > 0
      ? Number(detection.rally_window_ms)
      : rallyWindowMs(strokes);
  const clusters = buildStrokeClusters(strokes, gapMs);
  const bands = [];
  let rallyIdx = 0;
  clusters.forEach((cluster, essaiIdx0) => {
    const times = cluster
      .map((s) => strokePeakMs(s))
      .filter((t) => Number.isFinite(t));
    if (!times.length) return;
    const essaiIdx = essaiIdx0 + 1;
    const strokeCount = cluster.length;
    const isRally = strokeCount >= MIN_RALLY_STROKES;
    if (isRally) rallyIdx += 1;
    const t0 = Math.min(...times) / 1000;
    const t1 = Math.max(...times) / 1000;
    const label = isRally
      ? `T${essaiIdx}-R${rallyIdx}-${strokeCount}`
      : `T${essaiIdx}-${strokeCount}`;
    bands.push({
      key: `${keyPrefix}-cluster-${essaiIdx}`,
      kind: keyPrefix,
      essaiIndex: essaiIdx,
      rallyIndex: isRally ? rallyIdx : null,
      isRally,
      label,
      tStartSec: Math.max(0, t0 - RALLY_PAD_SEC),
      tEndSec: t1 + RALLY_PAD_SEC,
      strokeCount,
    });
  });
  return bands;
}

function strokeTimeWindowSec(stroke) {
  const phases = phasesOfStroke(stroke) || {};
  const starts = [
    Number(phases.preparation?.t_start_ms),
    Number(stroke?.t_valley_ms),
    Number(stroke?.t_contact_ms),
    Number(strokePeakMs(stroke)),
  ].filter((v) => Number.isFinite(v));
  const ends = [
    Number(phases.recovery?.t_end_ms),
    Number(strokePeakMs(stroke)),
    Number(stroke?.t_contact_ms),
  ].filter((v) => Number.isFinite(v));
  if (!starts.length || !ends.length) return null;
  const pad = 0.18;
  return [Math.max(0, Math.min(...starts) / 1000 - pad), Math.max(...ends) / 1000 + pad];
}

function phaseDurationMs(phase) {
  const t0 = Number(phase?.t_start_ms);
  const t1 = Number(phase?.t_end_ms);
  if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 < t0) return null;
  return Math.round(t1 - t0);
}

function filterOverlay(items, kinds, strokeIndex) {
  const list = Array.isArray(items) ? items : [];
  return list.filter((item) => {
    if (item.kind && kinds && !kinds.includes(item.kind)) return false;
    if (strokeIndex != null && item.strokeIndex != null && item.strokeIndex !== strokeIndex) {
      return false;
    }
    return true;
  });
}

const STROKE_RULES = {
  ts: {
    peak: "Forehand strokes tagged topspin (higher peak and amplitude than this player's FH)",
    prep: "Still counted as Forehand — this filter shows the tagged subset",
    fsm: "Compared to warmup topspin band, or the upper part of this player's FH if no topspin warmup yet",
  },
  fh: {
    peak: "All matching Prep→Peak→Recovery cycles count as Forehand",
    prep: "First stroke: Δx ≤ 0.15 × shoulder width. In a rally, angle peak + timing is enough even if Δx stays high.",
    fsm: "Topspin is extra peak+amplitude · bad placement is extra-low valley · missing trunk rotation is Δx outside this player's normal band",
  },
  bh: {
    peak: "All matching BH cycles count as Backhand; topspin is a tag on extra peak+amplitude",
    prep: "Contact: wrist above elbow (after BH prep) · personal bands from warmup or this clip",
    fsm: "Bad placement tags extra-low valley; isolated contacts far from ISI are dropped",
  },
  bhd: {
    peak: "Co-peak (~80 ms): shoulder-open + racket-elbow + hand–shoulder dist",
    prep: "Prep: racket hand below elbow → contact at the co-peak",
    fsm: "Counted as Backhand; topspin-tagged subset uses the BH topspin filter",
  },
  srv: {
    peak: "Toss hand = opposite racket · still palm at that player's table end",
    prep: "Start on/behind the end line — not over the table",
    fsm: "Vertical toss · rise ≥ 0.035 · edge ≤ 0.10 · other-side dominance · 2.2s spacing",
  },
};

function formatStrokeTag(tag, t) {
  if (tag === "topspin") return t("poseTagTopspin", "topspin");
  if (tag === "bad_placement") return t("poseTagBadPlacement", "bad placement");
  if (tag === "missing_trunk_rotation") {
    return t("poseTagMissingTrunkRotation", "missing trunk rotation");
  }
  return String(tag).replace(/_/g, " ");
}

function strokeTagAbbrev(tag) {
  if (tag === "topspin") return "TS";
  if (tag === "bad_placement") return "BP";
  if (tag === "missing_trunk_rotation") return "TR";
  return tag;
}

function playerPeakRule(kind, detection) {
  const th = detection?.player_thresholds || {};
  const sig = detection?.player_signature || th;
  const mode = th.applied_mode || sig?.peak;
  const lo = Number(mode?.lo);
  const hi = Number(mode?.hi);
  const from =
    th.calibrated_from === "warmup" || sig?.calibrated_from === "warmup"
      ? "calibrated from warmup"
      : "from this clip";
  const parts = [];
  if (Number.isFinite(lo) && Number.isFinite(hi)) {
    parts.push(`peak ${Math.round(lo)}°–${Math.round(hi)}° (min/max ±10%, ${from})`);
  } else {
    parts.push(from);
  }
  const prep = sig?.prep_angle || th.prep_angle;
  if (prep && Number.isFinite(Number(prep.lo)) && Number.isFinite(Number(prep.hi))) {
    parts.push(`prep ${Math.round(prep.lo)}°–${Math.round(prep.hi)}°`);
  }
  const dx = sig?.dx || th.dx;
  if (dx && Number.isFinite(Number(dx.lo)) && Number.isFinite(Number(dx.hi))) {
    parts.push(`Δx ${Number(dx.lo).toFixed(3)}–${Number(dx.hi).toFixed(3)}`);
  }
  const isi = sig?.isi_ms || th.isi_ms;
  if (isi && Number.isFinite(Number(isi.med))) {
    parts.push(`ISI ${Math.round(isi.med)} ms`);
  }
  if (parts.length) return `This player: ${parts.join(" · ")}`;
  return STROKE_RULES[kind]?.peak;
}

const PHASE_ORDER = [
  ["preparation", "Prep"],
  ["forward_swing", "Fwd"],
  ["contact", "Hit"],
  ["follow_through", "Follow"],
  ["recovery", "Rec"],
];

function StrokeDetail({ stroke, kind, index, t }) {
  if (!stroke) return null;
  const prefix = kind === "fh" ? "F" : kind === "bh" ? "B" : kind === "bhd" ? "D" : "";
  const kindLabel =
    kind === "ts"
      ? t("poseFhTopspinShortLabel", "FH topspin")
      : kind === "fh"
        ? t("poseFhShortLabel", "Forehand")
        : kind === "bhd"
          ? t("poseBhShortLabel", "Backhand")
          : t("poseBhTopspinShortLabel", "BH topspin");
  const fmtMs = (v) =>
    Number.isFinite(Number(v)) ? `${Math.round(Number(v))} ms` : "—";
  const tPeak = strokePeakMs(stroke);
  const phases = phasesOfStroke(stroke) || {};
  return (
    <div className="pose-angle-charts__stroke-detail" role="status">
      <div className="pose-angle-charts__params-title">
        {prefix}
        {index} · {kindLabel}
      </div>
      <div className="pose-angle-charts__stroke-detail-grid">
        <span>{t("poseStrokePeak", "Peak")}</span>
        <strong>{Number.isFinite(tPeak) ? formatSec(tPeak / 1000) : "—"}</strong>
        <span>{t("posePhaseContact", "Contact")}</span>
        <strong>
          {Number.isFinite(Number(stroke.t_contact_ms))
            ? formatSec(Number(stroke.t_contact_ms) / 1000)
            : "—"}
        </strong>
        <span>{t("poseMetricAvgAmplitude", "Amplitude")}</span>
        <strong>{fmtMs(stroke.amplitude_ms)}</strong>
        {Number.isFinite(Number(stroke.angle_deg)) ? (
          <>
            <span>{t("poseStrokePeakAngle", "Peak angle")}</span>
            <strong>{Math.round(Number(stroke.angle_deg))}°</strong>
          </>
        ) : null}
        {stroke.contact_source ? (
          <>
            <span>{t("poseStrokeContactSource", "Contact source")}</span>
            <strong>{String(stroke.contact_source).replace(/_/g, " ")}</strong>
          </>
        ) : null}
        {stroke.wrist_power != null ? (
          <>
            <span>{t("poseMetricWristPower", "Wrist power")}</span>
            <strong>{stroke.wrist_power ? t("yes", "Yes") : t("no", "No")}</strong>
          </>
        ) : null}
        {Array.isArray(stroke.tags) && stroke.tags.length ? (
          <>
            <span>{t("poseStrokeTags", "Tags")}</span>
            <strong>{stroke.tags.map((tg) => formatStrokeTag(tg, t)).join(", ")}</strong>
          </>
        ) : null}
      </div>
      <div className="pose-angle-charts__stroke-phases">
        {PHASE_ORDER.map(([key, fallback]) => {
          const dur = phaseDurationMs(phases[key]);
          if (dur == null) return null;
          return (
            <span key={key} className={`pose-angle-charts__stroke-phase pose-angle-charts__stroke-phase--${key}`}>
              {fallback} {dur} ms
            </span>
          );
        })}
      </div>
    </div>
  );
}

function fmtMs(v) {
  return v == null || !Number.isFinite(Number(v)) ? "—" : `${Math.round(v)} ms`;
}

/** Per-serve metric: contact → ready. Timeout / missing → null. */
function serveReadyMetric(event) {
  if (event?.ready_timeout) return null;
  const ms = Number(event?.time_to_ready_ms);
  return Number.isFinite(ms) ? Math.round(ms) : null;
}

function serveReadyMetricLabel(event, t) {
  const ms = serveReadyMetric(event);
  if (ms == null) return t("poseServeReadyTimeoutShort", "—");
  return `${ms} ms`;
}

function fmtPct(v) {
  return v == null || !Number.isFinite(Number(v)) ? "—" : `${Math.round(v)}%`;
}

function zoneLabel(event, t) {
  const lat = event?.lateral_zone;
  const depth = event?.depth_zone;
  const latTxt =
    lat === "wide_top"
      ? t("poseServeZoneWideTop", "wide (top)")
      : lat === "wide_bottom"
        ? t("poseServeZoneWideBottom", "wide (bottom)")
        : lat === "center"
          ? t("poseServeZoneCenter", "center")
          : null;
  const depthTxt =
    depth === "close"
      ? t("poseServeDepthClose", "close")
      : depth === "mid"
        ? t("poseServeDepthMid", "mid")
        : depth === "far"
          ? t("poseServeDepthFar", "far")
          : null;
  if (!latTxt && !depthTxt) return "—";
  return [depthTxt, latTxt].filter(Boolean).join(" · ");
}

function ServeMetrics({
  detection,
  t,
  selectedIndex = null,
  onSelectIndex = null,
  bounceHeatmap = null,
  serveMetrics = null,
}) {
  if (!detection) return null;
  const events = Array.isArray(detection.events) ? detection.events : [];
  const metrics = serveMetrics || {
    serve_try_count: detection.serve_try_count ?? detection.serve_count,
    serve_short_count: detection.serve_short_count,
    serve_long_count: detection.serve_long_count,
    serve_missed_count: detection.serve_missed_count,
  };
  const bouncePoints = Array.isArray(bounceHeatmap?.points)
    ? bounceHeatmap.points
    : [];
  const countA =
    Number(detection.serve_count_a) ||
    events.filter((e) => servePlayerOf(e) === "A").length;
  const countB =
    Number(detection.serve_count_b) ||
    events.filter((e) => servePlayerOf(e) === "B").length;
  const ready = detection.time_to_ready || {};
  const points = Array.isArray(detection.heat?.points) && detection.heat.points.length
    ? detection.heat.points
    : events
        .filter((e) => Number.isFinite(Number(e.hip_u)) && Number.isFinite(Number(e.hip_v)))
        .map((e) => ({
          player: servePlayerOf(e),
          index: e.index,
          u: e.hip_u,
          v: e.hip_v,
          time_to_ready_ms: e.time_to_ready_ms,
          ready_timeout: e.ready_timeout,
        }));
  return (
    <div
      className="pose-detection-metrics"
      role="status"
      aria-label={t("poseServeMetrics", "Serve toss")}
    >
      <div className="pose-detection-metrics__title">
        {t("poseServeMetrics", "Serve toss")}
      </div>
      <div className="pose-detection-metrics__grid">
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("serve_try_count", "Attempts")}
          </span>
          <span className="pose-detection-metrics__value">
            {Number(metrics.serve_try_count) || Number(detection.serve_count) || 0}
          </span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("serve_short_count", "Short")}
          </span>
          <span className="pose-detection-metrics__value">
            {Number(metrics.serve_short_count) || 0}
          </span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("serve_long_count", "Long")}
          </span>
          <span className="pose-detection-metrics__value">
            {Number(metrics.serve_long_count) || 0}
          </span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("serve_missed_count", "Missed")}
          </span>
          <span className="pose-detection-metrics__value">
            {Number(metrics.serve_missed_count) || 0}
          </span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("posePlayerA", "Player A")} ({t("poseServeLeft", "left")})
          </span>
          <span className="pose-detection-metrics__value">{countA}</span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("posePlayerB", "Player B")} ({t("poseServeRight", "right")})
          </span>
          <span className="pose-detection-metrics__value">{countB}</span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseServeReadyMedian", "Median contact → ready")}
          </span>
          <span className="pose-detection-metrics__value">{fmtMs(ready.median_ms)}</span>
        </div>
        <div className="pose-detection-metrics__stat">
          <span className="pose-detection-metrics__label">
            {t("poseServeReadyUnder800", "Ready < 0.8s")}
          </span>
          <span className="pose-detection-metrics__value">
            {fmtPct(ready.pct_ready_under_800)}
          </span>
        </div>
      </div>
      {bouncePoints.length ? (
        <ServeBounceHeatMap
          points={bouncePoints}
          metrics={metrics}
          ariaLabel={t("poseServeBounceHeatAria", "Serve bounce landings on the table")}
        />
      ) : null}
      {points.length ? (
        <ServeHeatMap
          points={points}
          selectedIndex={selectedIndex}
          onSelectIndex={onSelectIndex}
          labelA={t("posePlayerAShort", "A")}
          labelB={t("posePlayerBShort", "B")}
          ariaLabel={t("poseServeHeatAria", "Serve hip positions on the table")}
        />
      ) : null}
      {events.length ? (
        <ul className="serve-ready-list" aria-label={t("poseServeReadyListAria", "Contact to ready per serve")}>
          {events.map((event, i) => {
            const idx = Number(event?.index) || i + 1;
            const player = servePlayerOf(event);
            const selected = Number.isFinite(selectedIndex) && idx === selectedIndex;
            return (
              <li key={`srv-ready-${idx}`}>
                <button
                  type="button"
                  className={`serve-ready-list__row ${player === "B" ? "is-b" : "is-a"} ${
                    selected ? "is-on" : ""
                  }`}
                  aria-pressed={selected}
                  onClick={() => onSelectIndex?.(idx)}
                >
                  <span className="serve-ready-list__id">{serveLabel(event, idx)}</span>
                  <span className="serve-ready-list__metric-label">
                    {t("poseServeContactToReady", "Contact → ready")}
                  </span>
                  <strong className="serve-ready-list__value">
                    {serveReadyMetricLabel(event, t)}
                  </strong>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
      <p className="pose-detection-metrics__hint">
        {t(
          "poseMetricHintServe",
          "Heat = server hips at contact (A left, B right). Each serve’s metric is contact → ready (ms until the stance settles). SA# = Player A, SB# = Player B."
        )}
      </p>
    </div>
  );
}

function ServeDetail({ event, index, t }) {
  if (!event) return null;
  const tMs = Number(event?.t_peak_ms ?? event?.t_ms);
  const conf = Number(event?.confidence);
  const player = servePlayerOf(event);
  const sideLabel =
    player === "B"
      ? `${t("posePlayerB", "Player B")} (${t("poseServeRight", "right")})`
      : `${t("posePlayerA", "Player A")} (${t("poseServeLeft", "left")})`;
  return (
    <div className="pose-angle-charts__stroke-detail" role="status">
      <div className="pose-angle-charts__params-title">
        {serveLabel(event, index)} · {sideLabel}
      </div>
      <div className="pose-angle-charts__stroke-detail-grid">
        <span>{t("poseServeContactToReady", "Contact → ready")}</span>
        <strong>
          {event?.ready_timeout
            ? t("poseServeReadyTimeout", "no settle ≤ 1.5s")
            : fmtMs(event?.time_to_ready_ms)}
        </strong>
        <span>{t("poseServeToss", "Toss")}</span>
        <strong>{Number.isFinite(tMs) ? formatSec(tMs / 1000) : "—"}</strong>
        <span>{t("poseServeSide", "Server")}</span>
        <strong>{sideLabel}</strong>
        <span>{t("poseServeTossHand", "Toss hand")}</span>
        <strong>
          {event?.toss_hand === "right"
            ? t("poseServeTossRight", "Right (opp. racket)")
            : t("poseServeTossLeft", "Left (opp. racket)")}
        </strong>
        <span>{t("poseServeConfidence", "Confidence")}</span>
        <strong>
          {Number.isFinite(conf) ? `${Math.round(conf * 100)}%` : "—"}
        </strong>
        <span>{t("poseServeStanceZone", "Stance")}</span>
        <strong>{zoneLabel(event, t)}</strong>
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label, unit, phaseAtSec }) {
  if (!active || !payload?.length) return null;
  const phase =
    typeof phaseAtSec === "function" ? phaseAtSec(Number(label)) : null;
  return (
    <div className="pose-angle-charts__tooltip">
      <div className="pose-angle-charts__tooltip-time">{formatSec(label)}</div>
      {phase?.phaseLabel ? (
        <div
          className={`pose-angle-charts__tooltip-phase pose-angle-charts__tooltip-phase--${phase.phaseKey || "none"}`}
        >
          {phase.kindLabel ? `${phase.kindLabel} · ` : null}
          {phase.phaseLabel}
        </div>
      ) : null}
      {payload.map((p) =>
        p.value != null && Number.isFinite(p.value) ? (
          <div key={p.dataKey} style={{ color: p.color }}>
            {p.name}:{" "}
            {unit === "deg"
              ? `${Math.round(p.value)}°`
              : Number(p.value).toFixed(3)}
          </div>
        ) : null
      )}
    </div>
  );
}

function TimelineChart({
  data,
  lines,
  yDomain,
  xDomain,
  currentTimeSec,
  unit,
  height = 120,
  showBrush = false,
  onBrushChange,
  strokeMarkers = [],
  clusterBands = [],
  phaseBands = [],
  contactMarkers = [],
  serveMarkers = [],
  phaseAtSec = null,
}) {
  const playhead =
    Number.isFinite(currentTimeSec) && currentTimeSec >= 0 ? currentTimeSec : null;
  const visibleLines = lines.filter((l) => l.visible);
  const x0 = Array.isArray(xDomain) ? xDomain[0] : null;
  const x1 = Array.isArray(xDomain) ? xDomain[1] : null;
  const inView = (t) =>
    Number.isFinite(t) &&
    (x0 == null || x1 == null || (t >= x0 - 0.05 && t <= x1 + 0.05));
  const bandInView = (b) =>
    Number.isFinite(b.tStartSec) &&
    Number.isFinite(b.tEndSec) &&
    (x0 == null ||
      x1 == null ||
      (b.tEndSec >= x0 - 0.05 && b.tStartSec <= x1 + 0.05));

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.08)" vertical={false} />
        <XAxis
          dataKey="tSec"
          type="number"
          domain={xDomain}
          allowDataOverflow
          tick={{ fill: "rgba(248,250,252,0.45)", fontSize: 10 }}
          tickLine={false}
          axisLine={{ stroke: "rgba(255,255,255,0.12)" }}
          interval="preserveStartEnd"
          tickFormatter={formatSec}
        />
        <YAxis
          domain={yDomain}
          width={40}
          tick={{ fill: "rgba(248,250,252,0.45)", fontSize: 10 }}
          tickLine={false}
          axisLine={false}
          allowDecimals={unit !== "deg"}
          tickFormatter={(v) => (unit === "deg" ? `${v}°` : Number(v).toFixed(2))}
        />
        <Tooltip
          content={(props) => (
            <ChartTooltip {...props} unit={unit} phaseAtSec={phaseAtSec} />
          )}
        />
        {clusterBands.map((b) =>
          bandInView(b) ? (
            <ReferenceArea
              key={b.key}
              x1={b.tStartSec}
              x2={b.tEndSec}
              fill={b.isRally ? COLOR_RALLY_FILL : COLOR_TRIAL_FILL}
              stroke={b.isRally ? COLOR_RALLY_STROKE : COLOR_TRIAL_STROKE}
              strokeOpacity={1}
              strokeWidth={b.isRally ? 1.5 : 1}
              ifOverflow="hidden"
              label={{
                value: b.label,
                position: "insideTop",
                fill: b.isRally ? COLOR_RALLY_LABEL : COLOR_TRIAL_LABEL,
                fontSize: 11,
                fontWeight: 700,
              }}
            />
          ) : null
        )}
        {strokeMarkers.map((m) =>
          inView(m.tPeakSec) ? (
            <ReferenceLine
              key={m.key || `stroke-${m.index}`}
              x={m.tPeakSec}
              stroke={m.color || COLOR_STROKE}
              strokeWidth={m.emphasis ? 2.4 : 1.5}
              ifOverflow="hidden"
              label={{
                value: `${m.labelPrefix || ""}${m.index}`,
                position: "insideTopLeft",
                fill: m.color || COLOR_STROKE,
                fontSize: 10,
                fontWeight: 700,
              }}
            />
          ) : null
        )}

        {phaseBands.map((b) =>
          bandInView(b) ? (
            <ReferenceArea
              key={b.key}
              x1={b.tStartSec}
              x2={b.tEndSec}
              fill={b.fill}
              stroke={b.stroke}
              strokeOpacity={1}
              strokeWidth={1}
              ifOverflow="hidden"
            />
          ) : null
        )}
        {contactMarkers.map((m) =>
          inView(m.tContactSec) ? (
            <ReferenceLine
              key={m.key}
              x={m.tContactSec}
              stroke={m.stroke}
              strokeWidth={1}
              ifOverflow="hidden"
            />
          ) : null
        )}
        {serveMarkers.map((m) =>
          inView(m.tPeakSec) ? (
            <ReferenceLine
              key={m.key || `serve-${m.index}`}
              x={m.tPeakSec}
              stroke={m.color || COLOR_SERVE}
              strokeWidth={m.emphasis ? 2.4 : 1.6}
              strokeDasharray="5 3"
              ifOverflow="hidden"
              label={{
                value: m.readyMs != null
                  ? `${m.label || `S${m.index}`} ${m.readyMs}`
                  : m.label || `S${m.index}`,
                position: "insideBottomLeft",
                fill: m.color || COLOR_SERVE,
                fontSize: 10,
                fontWeight: 700,
              }}
            />
          ) : null
        )}
        {playhead != null ? (
          <ReferenceLine
            x={playhead}
            stroke="#f8fafc"
            strokeWidth={2}
            ifOverflow="extendDomain"
          />
        ) : null}
        {unit === "norm" || (unit === "deg" && yDomain?.[0] < 0) ? (
          <ReferenceLine
            y={0}
            stroke="rgba(248,250,252,0.35)"
            strokeDasharray="4 4"
            ifOverflow="extendDomain"
          />
        ) : null}
        {visibleLines.map((line) => (
          <Line
            key={line.key}
            type="monotone"
            dataKey={line.key}
            name={line.name}
            stroke={line.color}
            strokeWidth={2}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
        {showBrush ? (
          <Brush
            dataKey="tSec"
            height={20}
            stroke="rgba(148, 163, 184, 0.7)"
            fill="rgba(30, 41, 59, 0.85)"
            travellerWidth={8}
            tickFormatter={formatSec}
            onChange={onBrushChange}
          />
        ) : null}
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Top pose charts: racket elbow angle + signed shoulder x delta.
 * Supports per-curve toggles and parent-controlled visibility.
 */
export function PoseAngleCharts({
  samples,
  currentTimeSec = 0,
  visible = true,
  onVisibleChange,
  viewSetup = null,
  fhTopspinDetection = null,
  fhDetection = null,
  bhTopspinDetection = null,
  bhLeftDetection = null,
  serveDetection = null,
  bounceHeatmap = null,
  serveMetrics = null,
  onSeek = null,
}) {
  const { t } = useTranslation();
  // Defer first paint so toggling Charts: On does not sync-block the video thread.
  const [chartsReady, setChartsReady] = useState(false);
  useEffect(() => {
    if (!visible) {
      setChartsReady(false);
      return undefined;
    }
    setChartsReady(false);
    let cancelled = false;
    const id =
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              if (!cancelled) setChartsReady(true);
            });
          })
        : window.setTimeout(() => {
            if (!cancelled) setChartsReady(true);
          }, 32);
    return () => {
      cancelled = true;
      if (typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(id);
      } else {
        window.clearTimeout(id);
      }
    };
  }, [visible, samples]);

  const data = useMemo(
    () =>
      chartsReady ? buildPoseAngleChartData(samples, viewSetup) : [],
    [samples, viewSetup, chartsReady]
  );
  const fullRangeRef = useRef([0, 1]);
  const [zoomDomain, setZoomDomain] = useState(null);
  const [curves, setCurves] = useState(DEFAULT_CURVES);
  const [strokeKind, setStrokeKind] = useState("all");
  const [selectedStrokeKey, setSelectedStrokeKey] = useState(null);

  useEffect(() => {
    setZoomDomain(null);
    setStrokeKind("all");
    setSelectedStrokeKey(null);
    setCurves(curvesForStrokeKind("all"));
  }, [samples]);

  const playerAName = t("posePlayerA", "Player A");
  const playerBName = t("posePlayerB", "Player B");

  const curveDefs = useMemo(
    () => [
      {
        key: "playerARacketShoulder",
        name: `${playerAName} ${t(
          "poseRacketShoulderOpen",
          "shoulder-open"
        )}`,
        color: COLOR_A_SHOULDER,
        group: "racket",
      },
      {
        key: "playerARacketElbowInterior",
        name: `${playerAName} ${t(
          "poseRacketElbowInterior",
          "racket-elbow"
        )}`,
        color: COLOR_STROKE_BHD,
        group: "racket",
      },
      {
        key: "playerAShoulderSpan",
        name: `${playerAName} ${t("poseShoulderSpan", "shoulder Δx")}`,
        color: COLOR_A_SPAN,
        group: "span",
      },
      {
        key: "playerAHandElbowDx",
        name: `${playerAName} ${t("poseHandElbowDx", "wrist−elbow Δx")}`,
        color: COLOR_A_HE_DX,
        group: "span",
      },
      {
        key: "playerAHandElbowDy",
        name: `${playerAName} ${t("poseHandElbowDy", "wrist−elbow Δy")}`,
        color: COLOR_A_SW,
        group: "span",
      },
      {
        key: "playerAShoulderWrist",
        name: `${playerAName} ${t("poseShoulderWrist", "shoulder−wrist")}`,
        color: "#e879f9",
        group: "span",
      },
      {
        key: "playerATossWristY",
        name: `${playerAName} ${t("poseTossWristY", "toss wrist y")}`,
        color: COLOR_A,
        group: "span",
      },
      {
        key: "playerBTossWristY",
        name: `${playerBName} ${t("poseTossWristY", "toss wrist y")}`,
        color: COLOR_B,
        group: "span",
      },
      {
        key: "playerATossWristX",
        name: `${playerAName} ${t("poseTossWristX", "toss wrist x")}`,
        color: COLOR_A_SPAN,
        group: "span",
      },
      {
        key: "playerBTossWristX",
        name: `${playerBName} ${t("poseTossWristX", "toss wrist x")}`,
        color: COLOR_B_SPAN,
        group: "span",
      },
    ],
    [playerAName, playerBName, t]
  );

  const kindCurveKeys = useMemo(
    () => new Set(STROKE_KIND_CURVE_KEYS[strokeKind] || STROKE_KIND_CURVE_KEYS.all),
    [strokeKind]
  );
  const visibleCurveDefs = useMemo(
    () => curveDefs.filter((c) => kindCurveKeys.has(c.key)),
    [curveDefs, kindCurveKeys]
  );

  const racketLines = useMemo(
    () =>
      visibleCurveDefs
        .filter((c) => c.group === "racket")
        .map((c) => ({ ...c, visible: Boolean(curves[c.key]) })),
    [visibleCurveDefs, curves]
  );

  const spanLines = useMemo(
    () =>
      visibleCurveDefs
        .filter((c) => c.group === "span")
        .map((c) => ({ ...c, visible: Boolean(curves[c.key]) })),
    [visibleCurveDefs, curves]
  );

  const racketDomain = useMemo(
    () =>
      angleDomain(
        data.flatMap((d) => [
          curves.playerARacketShoulder ? d.playerARacketShoulder : null,
          curves.playerARacketElbowInterior
            ? d.playerARacketElbowInterior
            : null,
        ]),
        {
          pad: 10,
          minSpan: 40,
          allowNegative: Boolean(curves.playerARacketShoulder),
        }
      ),
    [data, curves]
  );

  const spanDomain = useMemo(
    () =>
      numericDomain(
        data.flatMap((d) => [
          curves.playerAShoulderSpan ? d.playerAShoulderSpan : null,
          curves.playerAHandElbowDx ? d.playerAHandElbowDx : null,
          curves.playerAHandElbowDy ? d.playerAHandElbowDy : null,
          curves.playerAShoulderWrist ? d.playerAShoulderWrist : null,
          curves.playerATossWristX ? d.playerATossWristX : null,
          curves.playerATossWristY ? d.playerATossWristY : null,
          curves.playerBTossWristX ? d.playerBTossWristX : null,
          curves.playerBTossWristY ? d.playerBTossWristY : null,
        ])
      ),
    [data, curves]
  );

  const strokeMarkersAll = useMemo(() => {
    const topspin = strokeMarkersFromDetection(fhTopspinDetection, {
      color: COLOR_STROKE,
      keyPrefix: "ts",
    });
    const fh = strokeMarkersFromDetection(fhDetection, {
      color: COLOR_STROKE_FH,
      keyPrefix: "fh",
    });
    const bh = strokeMarkersFromDetection(bhTopspinDetection, {
      color: COLOR_STROKE_BH,
      keyPrefix: "bh",
    });
    const bhd = strokeMarkersFromDetection(bhLeftDetection, {
      color: COLOR_STROKE_BHD,
      keyPrefix: "bhd",
    });
    return [...topspin, ...fh, ...bh, ...bhd];
  }, [fhTopspinDetection, fhDetection, bhTopspinDetection, bhLeftDetection]);

  const clusterBandsAll = useMemo(
    () => [
      ...clusterBandsFromDetection(fhTopspinDetection, { keyPrefix: "ts" }),
      ...clusterBandsFromDetection(fhDetection, { keyPrefix: "fh" }),
      ...clusterBandsFromDetection(bhTopspinDetection, { keyPrefix: "bh" }),
      ...clusterBandsFromDetection(bhLeftDetection, { keyPrefix: "bhd" }),
    ],
    [fhTopspinDetection, fhDetection, bhTopspinDetection, bhLeftDetection]
  );

  const phaseBandsAll = useMemo(
    () => [
      ...phaseBandsFromDetection(fhTopspinDetection, { keyPrefix: "ts" }),
      ...phaseBandsFromDetection(fhDetection, { keyPrefix: "fh" }),
      ...phaseBandsFromDetection(bhTopspinDetection, { keyPrefix: "bh" }),
      ...phaseBandsFromDetection(bhLeftDetection, { keyPrefix: "bhd" }),
    ],
    [fhTopspinDetection, fhDetection, bhTopspinDetection, bhLeftDetection]
  );

  const contactMarkersAll = useMemo(
    () => [
      ...contactMarkersFromDetection(fhTopspinDetection, { keyPrefix: "ts" }),
      ...contactMarkersFromDetection(fhDetection, { keyPrefix: "fh" }),
      ...contactMarkersFromDetection(bhTopspinDetection, { keyPrefix: "bh" }),
      ...contactMarkersFromDetection(bhLeftDetection, { keyPrefix: "bhd" }),
    ],
    [fhTopspinDetection, fhDetection, bhTopspinDetection, bhLeftDetection]
  );

  const serveMarkersAll = useMemo(
    () => serveMarkersFromDetection(serveDetection),
    [serveDetection]
  );

  const strokeKindOptions = useMemo(() => {
    return [
      {
        id: "all",
        label: t("poseStrokeKindAll", "All strokes"),
        color: "#e2e8f0",
        count:
          (Number(fhDetection?.stroke_count) || 0) +
          (Number(bhLeftDetection?.stroke_count) || 0) +
          (Number(serveDetection?.serve_count) || 0),
      },
      {
        id: "ts",
        label: t("poseFhTopspinShortLabel", "FH topspin"),
        color: COLOR_STROKE,
        count: Number(fhTopspinDetection?.stroke_count) || 0,
      },
      {
        id: "fh",
        label: t("poseFhShortLabel", "Forehand"),
        color: COLOR_STROKE_FH,
        count: Number(fhDetection?.stroke_count) || 0,
      },
      {
        id: "bh",
        label: t("poseBhTopspinShortLabel", "BH topspin"),
        color: COLOR_STROKE_BH,
        count: Number(bhTopspinDetection?.stroke_count) || 0,
      },
      {
        id: "bhd",
        label: t("poseBhShortLabel", "Backhand"),
        color: COLOR_STROKE_BHD,
        count: Number(bhLeftDetection?.stroke_count) || 0,
      },
      {
        id: "srv",
        label: t("poseServeShortLabel", "Serve"),
        color: COLOR_SERVE,
        count: Number(serveDetection?.serve_count) || 0,
      },
    ];
  }, [t, fhTopspinDetection, fhDetection, bhTopspinDetection, bhLeftDetection, serveDetection]);

  const activeKinds = useMemo(
    () => (strokeKind === "all" ? ["fh", "bhd", "srv"] : [strokeKind]),
    [strokeKind]
  );
  const selectedKind = selectedStrokeKey
    ? String(selectedStrokeKey).split(":")[0]
    : null;
  const selectedIndex = selectedStrokeKey
    ? Number(String(selectedStrokeKey).split(":")[1])
    : null;
  const selectedStroke =
    selectedKind === "ts"
      ? fhTopspinDetection?.strokes?.[selectedIndex - 1]
      : selectedKind === "fh"
        ? fhDetection?.strokes?.[selectedIndex - 1]
        : selectedKind === "bh"
          ? bhTopspinDetection?.strokes?.[selectedIndex - 1]
          : selectedKind === "bhd"
            ? bhLeftDetection?.strokes?.[selectedIndex - 1]
          : null;
  const selectedServe =
    selectedKind === "srv"
      ? serveDetection?.events?.[selectedIndex - 1] || null
      : null;
  const selectedDetection =
    strokeKind === "ts"
      ? fhTopspinDetection
      : strokeKind === "fh"
        ? fhDetection
        : strokeKind === "bh"
          ? bhTopspinDetection
          : strokeKind === "bhd"
            ? bhLeftDetection
          : strokeKind === "srv"
            ? serveDetection
            : null;

  const strokeMarkers = useMemo(() => {
    const filtered = filterOverlay(
      strokeMarkersAll,
      activeKinds,
      Number.isFinite(selectedIndex) ? selectedIndex : null
    );
    if (!Number.isFinite(selectedIndex)) return filtered;
    return filtered.map((m) => ({ ...m, emphasis: true }));
  }, [strokeMarkersAll, activeKinds, selectedIndex]);

  const clusterBands = useMemo(() => {
    if (Number.isFinite(selectedIndex)) return [];
    return filterOverlay(clusterBandsAll, activeKinds, null);
  }, [clusterBandsAll, activeKinds, selectedIndex]);

  const phaseBands = useMemo(() => {
    const filtered = filterOverlay(
      phaseBandsAll,
      activeKinds,
      Number.isFinite(selectedIndex) ? selectedIndex : null
    );
    return filtered.length > 120 ? filtered.slice(0, 120) : filtered;
  }, [phaseBandsAll, activeKinds, selectedIndex]);

  const contactMarkers = useMemo(
    () =>
      filterOverlay(
        contactMarkersAll,
        activeKinds,
        Number.isFinite(selectedIndex) ? selectedIndex : null
      ),
    [contactMarkersAll, activeKinds, selectedIndex]
  );

  const serveMarkers = useMemo(() => {
    if (!activeKinds.includes("srv")) return [];
    if (!Number.isFinite(selectedIndex) || selectedKind !== "srv") {
      return serveMarkersAll;
    }
    return serveMarkersAll
      .filter((m) => m.index === selectedIndex)
      .map((m) => ({ ...m, emphasis: true }));
  }, [serveMarkersAll, activeKinds, selectedIndex, selectedKind]);

  const strokeChips = useMemo(() => {
    if (strokeKind === "all") return [];
    if (strokeKind === "srv") {
      const events = Array.isArray(serveDetection?.events)
        ? serveDetection.events
        : [];
      return events.map((event, i) => {
        const player = servePlayerOf(event);
        return {
          key: `srv:${i + 1}`,
          label: `${serveLabel(event, i + 1)} · ${serveReadyMetricLabel(event, t)}`,
          color: player === "B" ? COLOR_B : COLOR_A,
          event,
          index: i + 1,
        };
      });
    }
    const det = selectedDetection;
    const strokes = Array.isArray(det?.strokes) ? det.strokes : [];
    const color =
      strokeKind === "ts"
        ? COLOR_STROKE
        : strokeKind === "fh"
          ? COLOR_STROKE_FH
          : strokeKind === "bhd"
            ? COLOR_STROKE_BHD
          : COLOR_STROKE_BH;
    const prefix =
      strokeKind === "fh" ? "F" : strokeKind === "bh" ? "B" : strokeKind === "bhd" ? "D" : "";
    return strokes.map((stroke, i) => {
      const tags = Array.isArray(stroke?.tags) ? stroke.tags : [];
      const tagBit = tags.map((tg) => strokeTagAbbrev(tg)).filter(Boolean).join(" ");
      return {
        key: `${strokeKind}:${i + 1}`,
        label: tagBit ? `${prefix}${i + 1} · ${tagBit}` : `${prefix}${i + 1}`,
        color:
          tags.includes("topspin") && strokeKind === "fh" ? COLOR_STROKE : color,
        stroke,
        index: i + 1,
      };
    });
  }, [strokeKind, selectedDetection, serveDetection, t]);

  const phaseAtSec = useCallback(
    (tSec) => {
      const tMs = Number(tSec) * 1000;
      if (!Number.isFinite(tMs)) return null;
      const phaseLabelOf = (key) => {
        if (key === "preparation") return t("posePhasePreparation", "Preparation");
        if (key === "forward_swing") return t("posePhaseForwardSwing", "Forward swing");
        if (key === "contact") return t("posePhaseContact", "Contact");
        if (key === "follow_through") return t("posePhaseFollowThrough", "Follow-through");
        if (key === "recovery") return t("posePhaseRecovery", "Recovery");
        return key;
      };
      const kindLabelOf = (id) => {
        if (id === "fh_topspin_left" || id === "ts") {
          return t("poseFhTopspinShortLabel", "FH topspin");
        }
        if (id === "fh_left" || id === "fh") {
          return t("poseFhShortLabel", "Forehand");
        }
        if (id === "bh_topspin_left" || id === "bh") {
          return t("poseBhTopspinShortLabel", "BH topspin");
        }
        if (id === "bh_left" || id === "bhd") {
          return t("poseBhShortLabel", "Backhand");
        }
        return null;
      };
      if (strokeKind === "srv") {
        const events = Array.isArray(serveDetection?.events)
          ? serveDetection.events
          : [];
        const list =
          Number.isFinite(selectedIndex) && selectedKind === "srv"
            ? events.filter((_, i) => i + 1 === selectedIndex)
            : events;
        let best = null;
        let bestDt = 450;
        for (const ev of list) {
          const evT = Number(ev?.t_peak_ms ?? ev?.t_ms);
          if (!Number.isFinite(evT)) continue;
          const dt = Math.abs(evT - tMs);
          if (dt < bestDt) {
            bestDt = dt;
            best = ev;
          }
        }
        if (!best) return null;
        return {
          phaseKey: "contact",
          phaseLabel: t("poseServeToss", "Toss"),
          kindLabel: serveLabel(best),
        };
      }
      const scopedStrokes =
        Number.isFinite(selectedIndex) && selectedStroke
          ? [selectedStroke]
          : strokeKind !== "all" && Array.isArray(selectedDetection?.strokes)
            ? selectedDetection.strokes
            : null;
      const hit = scopedStrokes
        ? resolveFhTopspinPhaseAtMs({ strokes: scopedStrokes }, tMs)
        : resolveFhPhaseAtMs(
            {
              fh_topspin_left: fhTopspinDetection,
              fh_left: fhDetection,
              bh_topspin_left: bhTopspinDetection,
              bh_left: bhLeftDetection,
            },
            tMs
          );
      if (!hit?.phaseKey) return null;
      return {
        phaseKey: hit.phaseKey,
        phaseLabel: phaseLabelOf(hit.phaseKey),
        kindLabel:
          kindLabelOf(hit.detectionId) ||
          kindLabelOf(strokeKind) ||
          hit.label ||
          null,
      };
    },
    [
      bhLeftDetection,
      bhTopspinDetection,
      fhDetection,
      fhTopspinDetection,
      selectedDetection,
      selectedIndex,
      selectedKind,
      selectedStroke,
      serveDetection,
      strokeKind,
      t,
    ]
  );

  const selectStrokeKind = useCallback((id) => {
    setStrokeKind(id);
    setSelectedStrokeKey(null);
    setZoomDomain(null);
    setCurves(curvesForStrokeKind(id));
  }, []);

  const selectStrokeChip = useCallback(
    (chip) => {
      if (selectedStrokeKey === chip.key) {
        setSelectedStrokeKey(null);
        setZoomDomain(null);
        return;
      }
      setSelectedStrokeKey(chip.key);
      if (chip.event) {
        const tMs = Number(chip.event?.t_peak_ms ?? chip.event?.t_ms);
        if (Number.isFinite(tMs)) {
          const tSec = tMs / 1000;
          setZoomDomain([Math.max(0, tSec - 1.2), tSec + 1.2]);
          if (typeof onSeek === "function") onSeek(tSec, { pause: true });
        }
        return;
      }
      const win = strokeTimeWindowSec(chip.stroke);
      if (win && win[1] - win[0] >= 0.25) setZoomDomain(win);
      const tPeak = strokePeakMs(chip.stroke);
      if (typeof onSeek === "function" && Number.isFinite(tPeak)) {
        onSeek(tPeak / 1000, { pause: true });
      }
    },
    [selectedStrokeKey, onSeek]
  );

  const fullRange = useMemo(() => {
    if (!data.length) return [0, 1];
    const t0 = data[0].tSec;
    const t1 = data[data.length - 1].tSec;
    return [t0, t0 + Math.max(0.1, t1 - t0)];
  }, [data]);

  fullRangeRef.current = fullRange;

  const xDomain = useMemo(() => {
    if (zoomDomain && zoomDomain[1] > zoomDomain[0]) return zoomDomain;
    return fullRange;
  }, [zoomDomain, fullRange]);

  const zoomed = Boolean(
    zoomDomain &&
      (zoomDomain[0] > fullRange[0] + 0.01 || zoomDomain[1] < fullRange[1] - 0.01)
  );

  const resetZoom = useCallback(() => setZoomDomain(null), []);

  const toggleCurve = useCallback((key) => {
    setCurves((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const onBrushChange = useCallback(
    (range) => {
      if (!range || range.startIndex == null || range.endIndex == null) return;
      const start = data[range.startIndex];
      const end = data[range.endIndex];
      if (!start || !end || end.tSec - start.tSec < 0.2) return;
      setZoomDomain([start.tSec, end.tSec]);
    },
    [data]
  );

  const onWheel = useCallback(
    (e) => {
      const inPlot = e.target?.closest?.(
        ".pose-angle-charts__panel, .recharts-wrapper"
      );
      if (!inPlot) return;
      e.preventDefault();
      e.stopPropagation();
      const [f0, f1] = fullRangeRef.current;
      const fullSpan = Math.max(0.1, f1 - f0);
      const cur = zoomDomain || [f0, f1];
      const [z0, z1] = cur;
      const span = Math.max(0.15, z1 - z0);
      const rect = e.currentTarget.getBoundingClientRect();
      const rel = Math.min(
        1,
        Math.max(0, (e.clientX - rect.left - 40) / Math.max(1, rect.width - 52))
      );
      const pivot = z0 + span * rel;
      const factor = e.deltaY > 0 ? 1.18 : 1 / 1.18;
      let nextSpan = Math.min(fullSpan, Math.max(0.4, span * factor));
      let n0 = pivot - nextSpan * rel;
      let n1 = n0 + nextSpan;
      if (n0 < f0) {
        n0 = f0;
        n1 = f0 + nextSpan;
      }
      if (n1 > f1) {
        n1 = f1;
        n0 = f1 - nextSpan;
      }
      n0 = Math.max(f0, n0);
      n1 = Math.min(f1, n1);
      if (n1 - n0 >= fullSpan - 0.02) setZoomDomain(null);
      else setZoomDomain([n0, n1]);
    },
    [zoomDomain]
  );

  // React registers onWheel as passive; attach non-passive so preventDefault works.
  const chartsRootRef = useRef(null);
  useEffect(() => {
    const el = chartsRootRef.current;
    if (!el) return undefined;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  if (!visible) return null;
  if (!chartsReady || !data.length) {
    return (
      <section
        className="pose-angle-charts pose-angle-charts--top"
        aria-label={t("poseAngleCharts", "Pose charts")}
        aria-busy="true"
      >
        <div className="pose-angle-charts__header">
          <div className="pose-angle-charts__title">
            {t("poseChartsTitle", "Pose charts")}
          </div>
          <div className="pose-angle-charts__actions">
            <span className="pose-angle-charts__hint">
              {t("poseChartsPreparing", "Preparing charts…")}
            </span>
            {typeof onVisibleChange === "function" ? (
              <button
                type="button"
                className="pose-angle-charts__reset"
                onClick={() => onVisibleChange(false)}
              >
                {t("poseChartsHide", "Hide charts")}
              </button>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  const showRacket = racketLines.some((l) => l.visible);
  const showSpan = spanLines.some((l) => l.visible);

  return (
    <section
      ref={chartsRootRef}
      className="pose-angle-charts pose-angle-charts--top"
      aria-label={t("poseAngleCharts", "Pose charts")}
    >
      <div className="pose-angle-charts__header">
        <div className="pose-angle-charts__title">
          {t("poseChartsTitle", "Pose charts")}
        </div>
        <div className="pose-angle-charts__actions">
          <span className="pose-angle-charts__hint">
            {t("poseChartZoomHint", "Scroll to zoom · drag brush to select")}
          </span>
          {zoomed ? (
            <button
              type="button"
              className="pose-angle-charts__reset"
              onClick={resetZoom}
            >
              {t("poseChartResetZoom", "Reset zoom")}
            </button>
          ) : null}
          {typeof onVisibleChange === "function" ? (
            <button
              type="button"
              className="pose-angle-charts__reset"
              onClick={() => onVisibleChange(false)}
            >
              {t("poseChartsHide", "Hide charts")}
            </button>
          ) : null}
        </div>
      </div>

      <div
        className="pose-angle-charts__stroke-select"
        role="group"
        aria-label={t("poseStrokeSelect", "Stroke type")}
      >
        <span className="pose-angle-charts__select-label">
          {t("poseStrokeSelect", "Stroke type")}
        </span>
        {strokeKindOptions.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={`pose-angle-charts__curve-btn ${
              strokeKind === opt.id ? "is-on" : "is-off"
            }`}
            style={{ "--curve-color": opt.color }}
            aria-pressed={strokeKind === opt.id}
            onClick={() => selectStrokeKind(opt.id)}
          >
            <span
              className="pose-angle-charts__curve-swatch"
              style={{ background: opt.color }}
            />
            {opt.label}
            <span className="pose-angle-charts__stroke-count">{opt.count}</span>
          </button>
        ))}
      </div>

      <div className="pose-angle-charts__curve-toggles" role="group">
        <span className="pose-angle-charts__select-label">
          {t("poseStrokeSignals", "Signals used")}
        </span>
        {visibleCurveDefs.map((c) => (
          <button
            key={c.key}
            type="button"
            className={`pose-angle-charts__curve-btn ${
              curves[c.key] ? "is-on" : "is-off"
            }`}
            style={{
              "--curve-color": c.color,
            }}
            aria-pressed={Boolean(curves[c.key])}
            onClick={() => toggleCurve(c.key)}
          >
            <span
              className="pose-angle-charts__curve-swatch"
              style={{ background: c.color }}
            />
            {c.name}
          </button>
        ))}
      </div>

      {strokeKind !== "all" && STROKE_RULES[strokeKind] ? (
        <div className="pose-angle-charts__params" role="note">
          <div className="pose-angle-charts__params-title">
            {t("poseStrokeParamsUsed", "Parameters used")}
          </div>
          <ul className="pose-angle-charts__params-list">
            <li>{playerPeakRule(strokeKind, selectedDetection)}</li>
            <li>{STROKE_RULES[strokeKind].prep}</li>
            <li>{STROKE_RULES[strokeKind].fsm}</li>
            {strokeKind !== "srv" ? (
              <li>
                {t(
                  "poseStrokeParamsShared",
                  "Amplitude 40–1200 ms · rally ≥ 3 strokes · window = avg peak gap × 1.25"
                )}
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {strokeChips.length > 0 ? (
        <div
          className="pose-angle-charts__stroke-chips"
          role="group"
          aria-label={
            strokeKind === "srv"
              ? t("poseServePickOne", "Select a serve")
              : t("poseStrokePickOne", "Select a stroke")
          }
        >
          {strokeChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              className={`pose-angle-charts__stroke-chip ${
                selectedStrokeKey === chip.key ? "is-on" : ""
              }`}
              style={{ "--curve-color": chip.color }}
              aria-pressed={selectedStrokeKey === chip.key}
              onClick={() => selectStrokeChip(chip)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      ) : null}

      {selectedStroke ? (
        <StrokeDetail
          stroke={selectedStroke}
          kind={selectedKind}
          index={selectedIndex}
          t={t}
        />
      ) : null}

      {selectedServe ? (
        <ServeDetail event={selectedServe} index={selectedIndex} t={t} />
      ) : null}

      {strokeKind === "all" ? (
        <>
          {fhTopspinDetection ? (
            <PoseDetectionMetrics
              detection={fhTopspinDetection}
              title={t("poseFhTopspinMetrics", "Forehand topspin (Player A)")}
            />
          ) : null}
          {fhDetection ? (
            <PoseDetectionMetrics
              detection={fhDetection}
              title={t("poseFhMetrics", "Forehand (Player A)")}
            />
          ) : null}
          {bhTopspinDetection ? (
            <PoseDetectionMetrics
              detection={bhTopspinDetection}
              title={t("poseBhTopspinMetrics", "Backhand topspin (Player A)")}
            />
          ) : null}
          {bhLeftDetection ? (
            <PoseDetectionMetrics
              detection={bhLeftDetection}
              title={t("poseBhMetrics", "Backhand (Player A)")}
            />
          ) : null}
          {serveDetection ? (
            <ServeMetrics
              detection={serveDetection}
              t={t}
              selectedIndex={selectedKind === "srv" ? selectedIndex : null}
              bounceHeatmap={bounceHeatmap}
              serveMetrics={serveMetrics}
              onSelectIndex={(idx) =>
                selectStrokeChip({
                  key: `srv:${idx}`,
                  event: serveDetection?.events?.[idx - 1],
                })
              }
            />
          ) : null}
        </>
      ) : strokeKind === "srv" && serveDetection ? (
        <ServeMetrics
          detection={serveDetection}
          t={t}
          selectedIndex={selectedIndex}
          bounceHeatmap={bounceHeatmap}
          serveMetrics={serveMetrics}
          onSelectIndex={(idx) =>
            selectStrokeChip({
              key: `srv:${idx}`,
              event: serveDetection?.events?.[idx - 1],
            })
          }
        />
      ) : selectedDetection ? (
        <PoseDetectionMetrics
          detection={selectedDetection}
          title={
            strokeKind === "ts"
              ? t("poseFhTopspinMetrics", "Forehand topspin (Player A)")
              : strokeKind === "fh"
                ? t("poseFhMetrics", "Forehand (Player A)")
                : strokeKind === "bhd"
                  ? t("poseBhMetrics", "Backhand (Player A)")
                : t("poseBhTopspinMetrics", "Backhand topspin (Player A)")
          }
        />
      ) : null}

      {strokeMarkers.length > 0 ||
      clusterBands.length > 0 ||
      phaseBands.length > 0 ||
      contactMarkers.length > 0 ||
      serveMarkers.length > 0 ? (
        <div
          className="pose-angle-charts__stroke-legend"
          aria-label={t("poseStrokeMarkers", "Stroke markers")}
        >
          {strokeMarkers.some((m) => m.color === COLOR_STROKE) ? (
            <>
              <span
                className="pose-angle-charts__stroke-swatch"
                aria-hidden
              />
              <span>
                {t(
                  "poseStrokePeakLegendTopspin",
                  "Orange # = FH topspin peak (>90°)"
                )}
              </span>
            </>
          ) : null}
          {strokeMarkers.some((m) => m.color === COLOR_STROKE_FH) ? (
            <>
              <span
                className="pose-angle-charts__stroke-swatch pose-angle-charts__stroke-swatch--fh"
                aria-hidden
              />
              <span>
                {t(
                  "poseStrokePeakLegendFh",
                  "Blue F# = forehand peak (>20° and <90°)"
                )}
              </span>
            </>
          ) : null}
          {strokeMarkers.some((m) => m.color === COLOR_STROKE_BH) ? (
            <>
              <span
                className="pose-angle-charts__stroke-swatch pose-angle-charts__stroke-swatch--bh"
                aria-hidden
              />
              <span>
                {t(
                  "poseStrokePeakLegendBh",
                  "Violet B# = backhand topspin peak"
                )}
              </span>
            </>
          ) : null}
          {serveMarkers.some((m) => m.player === "A") ? (
            <>
              <span
                className="pose-angle-charts__stroke-swatch pose-angle-charts__stroke-swatch--serve-a"
                aria-hidden
              />
              <span>
                {t(
                  "poseServeLegendA",
                  "Cyan SA# = Player A serve (court-left)"
                )}
              </span>
            </>
          ) : null}
          {serveMarkers.some((m) => m.player === "B") ? (
            <>
              <span
                className="pose-angle-charts__stroke-swatch pose-angle-charts__stroke-swatch--serve-b"
                aria-hidden
              />
              <span>
                {t(
                  "poseServeLegendB",
                  "Pink SB# = Player B serve (court-right)"
                )}
              </span>
            </>
          ) : null}
          {clusterBands.length > 0 ? (
            <>
              <span
                className="pose-angle-charts__stroke-swatch pose-angle-charts__stroke-swatch--trial"
                aria-hidden
              />
              <span>
                {t(
                  "poseTrialBandLegend",
                  "T#‑n = try (stroke count)"
                )}
              </span>
              <span
                className="pose-angle-charts__stroke-swatch pose-angle-charts__stroke-swatch--rally"
                aria-hidden
              />
              <span>
                {t(
                  "poseRallyBandLegend",
                  "T#‑R#‑n = rally (≥3 strokes)"
                )}
              </span>
            </>
          ) : null}

          {phaseBands.length > 0 || contactMarkers.length > 0 ? (
            <>
              <span
                className="pose-angle-charts__phase-swatch pose-angle-charts__phase-swatch--preparation"
                aria-hidden
              />
              <span>{t("posePhasePreparation", "Preparation")}</span>

              <span
                className="pose-angle-charts__phase-swatch pose-angle-charts__phase-swatch--forward"
                aria-hidden
              />
              <span>{t("posePhaseForwardSwing", "Forward swing")}</span>

              <span
                className="pose-angle-charts__phase-swatch pose-angle-charts__phase-swatch--contact"
                aria-hidden
              />
              <span>{t("posePhaseContact", "Contact")}</span>

              <span
                className="pose-angle-charts__phase-swatch pose-angle-charts__phase-swatch--follow"
                aria-hidden
              />
              <span>{t("posePhaseFollowThrough", "Follow-through")}</span>

              <span
                className="pose-angle-charts__phase-swatch pose-angle-charts__phase-swatch--recovery"
                aria-hidden
              />
              <span>{t("posePhaseRecovery", "Recovery")}</span>
            </>
          ) : null}
        </div>
      ) : null}

      {showRacket ? (
        <div className="pose-angle-charts__panel">
          <div className="pose-angle-charts__subtitle">
            {t("poseRacketAngleChart", "Angles used for this stroke")}
          </div>
          <TimelineChart
            data={data}
            lines={racketLines}
            yDomain={racketDomain}
            xDomain={xDomain}
            currentTimeSec={currentTimeSec}
            unit="deg"
            height={showSpan ? 112 : 132}
            showBrush={!showSpan}
            onBrushChange={onBrushChange}
            strokeMarkers={strokeMarkers}
            clusterBands={clusterBands}
            phaseBands={phaseBands}
            contactMarkers={contactMarkers}
            serveMarkers={serveMarkers}
            phaseAtSec={phaseAtSec}
          />
        </div>
      ) : null}

      {showSpan ? (
        <div className="pose-angle-charts__panel">
          <div className="pose-angle-charts__subtitle">
            {t(
              "poseShoulderSpanChart",
              "Signals used for this stroke (Δx / Δy / distance / toss)"
            )}
          </div>
          <TimelineChart
            data={data}
            lines={spanLines}
            yDomain={spanDomain}
            xDomain={xDomain}
            currentTimeSec={currentTimeSec}
            unit="norm"
            height={showRacket ? 112 : 132}
            showBrush
            onBrushChange={onBrushChange}
            strokeMarkers={strokeMarkers}
            clusterBands={clusterBands}
            phaseBands={phaseBands}
            contactMarkers={contactMarkers}
            serveMarkers={serveMarkers}
            phaseAtSec={phaseAtSec}
          />
        </div>
      ) : null}

      {!showRacket && !showSpan ? (
        <p className="pose-angle-charts__empty">
          {t("poseChartsNoCurves", "Turn on a curve above to plot.")}
        </p>
      ) : null}
    </section>
  );
}

export default PoseAngleCharts;
